/**
 * Module for communicating with Yealink DECT USB headsets through the native Yealink SDK
 * embedded in the Electron desktop shell (v29+).
 * - used when the app runs embedded via IPC; in a browser Yealink.ts (WebHID) is used instead
 * - the SDK is only started once the selected microphone is a Yealink: starting it loads native
 *   code into the shell's main process (and on macOS commits the shell to a hard exit at quit),
 *   a cost users without a Yealink must never pay. Once running it stays up for the session
 * - drives full call control with caller name/number on the device display
 * - loads a phonebook for the device: local extensions (colleagues) first, then the user's
 *   personal phonebook, then the shared phonebook - capped at 2500 entries. Delivery is
 *   pull-based (the device fetches the list on its own terms and MERGES every fetch), so a
 *   changed list is followed by a registration cycle that makes the device re-fetch it
 */

import globalEvents from '@/utilities/GlobalEvents'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import { useEmployeesStore } from '@/stores/Employees'
import { usePhonebookStore } from '@/stores/Phonebook'
import { useSettingsStore } from '@/stores/Settings'
import logger from '@/utilities/Logger'
import { parseUsbProductId, findMatchingYealinkHid, isYealinkMediaDevice } from './yealinkMatching'
import { MediaDeviceType } from '@/stores/MediaDevices.types'
import { PbxPhonebookContactPhoneNumberType, type PbxPhonebookContact } from '@/TcxApi/PbxPhonebook'
import type { WindowElectronAPI, YealinkCallActionEvent, YealinkContact, YealinkDeviceSnapshot, YealinkEvent, YealinkResult } from '@/stores/Electron.types'
import { ref, watch, type Ref } from 'vue'

/** The device phonebook holds at most ~2500 entries and overflow is silent - never push more */
const MAX_PHONEBOOK_ENTRIES = 2500

/** Collapse bursts of phonebook/employee changes into one push to the device */
const PHONEBOOK_SYNC_DEBOUNCE_MS = 5000

/** Delay before retrying a phonebook reload the Connecter refused */
const PHONEBOOK_RELOAD_RETRY_MS = 2000

/** Delay between call end and a deferred phonebook reload, so the on-hook ack reaches the device first */
const PHONEBOOK_RELOAD_AFTER_CALL_MS = 1000

/** Events sent to the SipClient when the user presses buttons on the device */
export type YealinkHeadsetEvent = 'ANSWER' | 'HANGUP' | 'REJECT' | 'HOLD' | 'UNHOLD' | 'MUTE' | 'UNMUTE' | 'DTMF'

/**
 * One call as it is mirrored on the device. The device has a single audio path and it follows the
 * call the SDK considers active - a call the device holds carries no audio in the headset - so
 * every call the app has is mirrored with its own cid and exactly the one the user is on is active.
 */
interface MirroredCall {
	/** Our cid for this call, stable for its lifetime */
	cid: number
	/** The call's state as the device knows it */
	state: 'ringing' | 'ringback' | 'active' | 'hold'
	/** The other party as shown on the device display */
	contact: YealinkContact
}

/** Key for a call the user dialed on the device itself - it exists before the SIP session does */
const PENDING_DIAL = '#pending-dial'

export default class YealinkElectron {
	static #instance: YealinkElectron // NOSONAR

	/** Is a Yealink headset in use */
	use: Ref<boolean> = ref(false)

	useDeviceName: Ref<string | undefined> = ref(undefined)

	private readonly ipc = (window as unknown as WindowElectronAPI).electronAPI

	/** All currently attached Yealink devices, keyed by SDK deviceId */
	private readonly devices = new Map<number, YealinkDeviceSnapshot>()

	/** Lifecycle of the SDK in the shell - 'failed' is terminal for the session, like the single attempt the eager start used to make */
	private sdkState: 'stopped' | 'starting' | 'running' | 'failed' = 'stopped'

	/** The device call control is bound to - the one matching the selected microphone */
	private activeDeviceId?: number = undefined

	/** We own the cid namespace - a simple counter, one cid per logical call */
	private cidCounter = 0

	/** Every call the device knows about, keyed by the SipClient session id (PENDING_DIAL until the session exists) */
	private readonly calls = new Map<string, MirroredCall>()

	/** Contacts as last pushed to the device - used to correlate device-dialed numbers back to full numbers */
	private pushedContacts: YealinkContact[] = []

	/** Fingerprint of the last successful phonebook push, so identical pushes are skipped */
	private lastPushedPhonebook = ''

	private phonebookSyncRunning = false

	private phonebookTimer?: ReturnType<typeof setTimeout> = undefined

	/** A phonebook reload is needed but a call was in progress - run it when the call ends */
	private phonebookReloadPending = false

	private eventCallback: (event: YealinkHeadsetEvent, value?: string, callId?: string) => void = () => { /** */ }

	/** Last logged bind outcome - bindDevice runs on every media device change, so only log changes */
	private lastBindLog = ''

	private constructor() {
		logger.debug('YealinkElectron: Constructor')

		globalEvents.on('MediaDeviceChange', event => {
			if (event.kind === MediaDeviceType.audioinput) { this.onMicrophoneChanged() }
		})

		globalEvents.on('MediaDeviceSelected', data => {
			if (data.type === MediaDeviceType.audioinput) { this.onMicrophoneChanged() }
		})

		globalEvents.on('StatusRequest', () => {
			globalEvents.emit('StatusReport', { key: 'yealink', value: this.use.value })
			globalEvents.emit('StatusReport', { key: 'yealinkIntegration', value: 'Electron SDK' })
			if (this.use.value) {
				globalEvents.emit('StatusReport', { key: 'yealinkDevice', value: this.useDeviceName.value as string })
			}
		})

		// Keep the device phonebook in sync with colleagues and the phonebook
		globalEvents.on('Phonebook:Reloaded', () => { this.schedulePhonebookSync() })
		globalEvents.on('Phonebook:Created', () => { this.schedulePhonebookSync() })
		globalEvents.on('Phonebook:Updated', () => { this.schedulePhonebookSync() })
		globalEvents.on('Phonebook:Deleted', () => { this.schedulePhonebookSync() })
		watch(() => useEmployeesStore().employees.length, () => { this.schedulePhonebookSync() })
		watch(() => usePhonebookStore().contacts.length, () => { this.schedulePhonebookSync() })

		// No SDK start here - onMicrophoneChanged() starts it the first time a Yealink microphone is selected
	}

	/** Get the YealinkElectron singleton instance */
	static get instance() {
		return YealinkElectron.#instance
	}

	/** Create the YealinkElectron singleton instance and return it */
	static create(eventCallback: (event: YealinkHeadsetEvent, value?: string, callId?: string) => void) {
		if (!YealinkElectron.#instance) {
			YealinkElectron.#instance = new YealinkElectron()
		}
		YealinkElectron.#instance.eventCallback = eventCallback
		return YealinkElectron.#instance
	}

	/** True when the app is embedded via IPC in a desktop shell that carries the Yealink SDK (v29+) */
	static isAvailable() {
		const ss = useSettingsStore()
		const ipc = (window as unknown as WindowElectronAPI).electronAPI
		const hasBridge = typeof ipc?.yealinkStart === 'function'
		const available = ss.runtime.isEmbeddedIpc && hasBridge

		if (available) {
			logger.info('YealinkElectron: Yealink headsets are driven through the native SDK in the desktop shell - the SDK is started when a Yealink microphone is selected')
		} else {
			logger.info(`YealinkElectron: Native SDK integration not available (embedded via IPC: ${ss.runtime.isEmbeddedIpc}, shell has Yealink bridge: ${hasBridge}) - Yealink headsets will use WebHID`)
		}

		return available
	}

	// #region Init

	/**
	 * The selected microphone or the set of audio inputs changed. Call control only ever binds to
	 * the SDK device matching the selected microphone, so the SDK is not needed - and not started -
	 * until that microphone is a Yealink. Once running, the selection just re-binds.
	 */
	private onMicrophoneChanged() {
		switch (this.sdkState) {
			case 'running':
				this.bindDevice()
				return
			case 'starting': // start() binds when it completes
			case 'failed': // one attempt per session - the failure was logged when it happened
				return
			case 'stopped': {
				const microphone = useMediaDevicesStore().microphoneDevice

				if (microphone && isYealinkMediaDevice(microphone)) {
					logger.info(`YealinkElectron: Selected microphone (${microphone.label}) is a Yealink - starting the SDK in the desktop shell`)
					void this.start()
				}
				return
			}
		}
	}

	/** Subscribe to events and start the SDK in the main process - runs once per session */
	private async start() {
		this.sdkState = 'starting'

		// Subscribe BEFORE starting so no deviceAttached events are missed
		this.ipc.onYealink(event => { this.onYealinkEvent(event) })

		const started = await this.ipc.yealinkStart()

		if (!started.success) {
			this.sdkState = 'failed'
			logger.error(`YealinkElectron: Failed to start the Yealink SDK - ${started.error?.message}`)
			return
		}

		this.sdkState = 'running'
		logger.info(`YealinkElectron: SDK ${started.result?.sdkVersion} started - ${started.result?.devices.length ?? 0} device(s) attached`)

		// The start result is the source of truth (also after a frontend reload)
		this.devices.clear()

		for (const device of started.result?.devices ?? []) {
			this.devices.set(device.info.deviceId, device)
			logger.info(`YealinkElectron: Attached: ${device.info.name} (deviceId ${device.info.deviceId})`)
		}
		this.bindDevice()
	}

	/**
	 * Bind call control to the attached device that matches the SELECTED audio device (matched on
	 * its USB product id). With two Yealinks connected, picking an arbitrary device would bind call
	 * control to whichever enumerated first - possibly the wrong headset.
	 */
	private bindDevice() {
		const mediaDevices = useMediaDevicesStore()
		const microphone = mediaDevices.microphoneDevice
		let device: YealinkDeviceSnapshot | undefined = undefined
		let noBindReason = ''

		if (microphone && isYealinkMediaDevice(microphone)) {
			const candidates = [...this.devices.values()].filter(candidate => !candidate.info.isInFirmwareUpdateMode)
			const matched = findMatchingYealinkHid(
				candidates.map(candidate => ({ vendorId: candidate.info.vendorId, productId: candidate.info.productId, productName: candidate.info.name, snapshot: candidate })),
				parseUsbProductId(microphone.productId)
			)

			// The audio interface can report a different product id than the SDK - with a single device attached it is safe to bind anyway
			device = matched?.snapshot ?? (candidates.length === 1 ? candidates[0] : undefined)

			if (!device) {
				noBindReason = `the selected microphone (${microphone.label}) is a Yealink but the SDK reports ` +
					(this.devices.size === 0 ? 'no attached devices' : `${this.devices.size} attached device(s), none matching it`)
			}
		} else if (this.devices.size > 0) {
			noBindReason = `${this.devices.size} Yealink device(s) attached but the selected microphone (${microphone?.label ?? 'none'}) is not a Yealink`
		}

		// Say why call control is off whenever there is something to say - but never repeat it (the
		// steady no-devices/no-Yealink-microphone state stays silent)
		if (!device && (noBindReason || this.activeDeviceId !== undefined)) {
			this.logBindOutcome(`YealinkElectron: Call control disabled - ${noBindReason || 'no Yealink devices attached'}`)
		}

		if (device?.info.deviceId === this.activeDeviceId) { return }

		// Release any call left on the device we are leaving
		if (this.activeDeviceId !== undefined) {
			for (const call of this.calls.values()) { void this.ipc.yealinkTerminateCall(this.activeDeviceId, call.cid) }
		}
		this.resetCallState()

		this.activeDeviceId = device?.info.deviceId
		this.use.value = !!device
		this.useDeviceName.value = device ? (device.info.isDongle && device.info.dongleName ? `${device.info.name} (${device.info.dongleName})` : device.info.name) : undefined

		if (device) {
			this.logBindOutcome(`YealinkElectron: Headset connected: ${this.useDeviceName.value} - deviceId ${device.info.deviceId}`)
			this.lastPushedPhonebook = '' // force a push to the newly bound device
			this.schedulePhonebookSync()
		}
	}

	/** Log a bind outcome, suppressing consecutive repeats of the same message */
	private logBindOutcome(message: string) {
		if (message === this.lastBindLog) { return }
		this.lastBindLog = message
		logger.info(message)
	}

	// #endregion

	// #region Events from the device

	private onYealinkEvent(event: YealinkEvent) {
		switch (event.type) {
			case 'deviceAttached':
				logger.info(`YealinkElectron: Device attached: ${event.device.info.name} (deviceId ${event.device.info.deviceId})`)
				this.devices.set(event.device.info.deviceId, event.device)
				this.bindDevice()
				break
			case 'deviceRemoved':
				logger.info(`YealinkElectron: Device removed: deviceId ${event.deviceId}`)
				this.devices.delete(event.deviceId)
				this.bindDevice()
				break
			case 'phonebookRequested':
				// Manager-level (any device, not just the bound one) and informational - the shell
				// auto-answers the request with the last list passed to yealinkSetPhonebook
				logger.info(`YealinkElectron: Device ${event.deviceId} requested the phonebook - the shell answers with the last loaded list`)
				break
			case 'callAction':
				this.onCallAction(event)
				break
			case 'batteryUpdate':
				if (event.low && !event.charging) {
					logger.info(`YealinkElectron: Headset battery is low - ${event.levelPercent}%`)
				}
				break
			default: // scanComplete, dialNumberChanged, hidInput, hidRaw, busylight, focusChange, firmwareProgress, settingsChanged - not used
				break
		}
	}

	/**
	 * The user pressed a call-control button on the device. This is a request, not a notification -
	 * we forward it to the SipClient, and the SDK is acknowledged when the SipClient calls back into
	 * offHook/onHook/hold/unhold/mute/unmute after applying the action to the SIP call.
	 */
	private onCallAction(event: YealinkCallActionEvent) {
		if (event.deviceId !== this.activeDeviceId) {
			logger.debug(`YealinkElectron: Ignoring ${event.actionName} from unbound device ${event.deviceId}`)
			return
		}

		// The device names the call it means - with more than one call on the display the SipClient
		// would otherwise have to guess which one the user pressed the button for
		const callId = [...this.calls.entries()].find(([, call]) => call.cid === event.cid)?.[0]

		logger.info(`YealinkElectron: User pressed ${event.actionName}${callId === undefined ? '' : ` for the call on cid ${event.cid}`}`)

		switch (event.actionName) {
			case 'Accept':
				this.eventCallback('ANSWER', undefined, callId)
				break
			case 'Reject':
			case 'Terminate': {
				// Acknowledge right away so the device always leaves the call state - even if no SIP
				// call remains. The later onHook() from the SipClient becomes a no-op because the
				// call is already gone from the mirror.
				const call = callId === undefined ? undefined : this.calls.get(callId)

				if (callId !== undefined) { this.calls.delete(callId) }
				if (call) { void this.ipc.yealinkTerminateCall(event.deviceId, call.cid) }
				this.runDeferredPhonebookReload()
				this.eventCallback(event.actionName === 'Reject' ? 'REJECT' : 'HANGUP', undefined, callId)
				break
			}
			case 'Hold':
				this.eventCallback('HOLD', undefined, callId)
				break
			case 'Resume':
				this.eventCallback('UNHOLD', undefined, callId)
				break
			case 'Mute':
				this.eventCallback('MUTE', undefined, callId)
				break
			case 'Unmute':
				this.eventCallback('UNMUTE', undefined, callId)
				break
			case 'DTMF':
				if (event.digit !== undefined) { this.eventCallback('DTMF', event.digit, callId) }
				break
			case 'CallOut':
				this.onDeviceCallOut(event)
				break
			default: // EnterDial, Redial - not used
				break
		}
	}

	/** The user dialed a number on the device (dialpad, phonebook or redial) */
	private onDeviceCallOut(event: YealinkCallActionEvent) {
		if (!event.number) {
			logger.info('YealinkElectron: CallOut without a number (phonebook/redial edge case) - ignoring')
			return
		}

		const ss = useSettingsStore()
		const digits = event.number.replace(/\D/g, '')
		let number = event.number
		let name: string | undefined = undefined

		// The device sends digits only - a leading + is lost. Picking a contact on the device
		// auto-types one of its stored numbers, so correlate back to the pushed contact to dial
		// the full number and show the right name.
		for (const contact of this.pushedContacts) {
			const match = [contact.number, contact.mobile, contact.other].find(num => num && num.replace(/\D/g, '') === digits)

			if (match) {
				number = match
				name = contact.name
				break
			}
		}

		if (number.startsWith('00')) { number = '+' + number.substring(2) }
		if (!number.startsWith('+') && number.length > 7 && ss.pbxSettings?.nationalPrefix) { number = ss.pbxSettings.nationalPrefix + number }

		logger.info(`YealinkElectron: User dialed ${event.number} on the device${number !== event.number ? ` - resolved to ${name ? name + ' ' : ''}${number}` : ''}`)

		// A dial the user did not follow through on leaves its call behind - drop it before the new one
		const abandoned = this.calls.get(PENDING_DIAL)

		if (abandoned) {
			this.calls.delete(PENDING_DIAL)
			void this.ipc.yealinkTerminateCall(event.deviceId, abandoned.cid)
		}

		// Move the device from dial-entry to ringback right away - the SIP progress finds the call
		// already in ringback and adopts it once the session it dialed exists
		const dialed: MirroredCall = { cid: this.nextCid(), state: 'ringback', contact: this.makeContact(name, number) }

		this.calls.set(PENDING_DIAL, dialed)
		void this.ipc.yealinkRingBack(event.deviceId, dialed.cid)

		globalEvents.emit('Call:Dial', { number })
	}

	// #endregion

	// #region Actions - public (called by the SipClient)

	/**
	 * Ring the headset for an incoming call, showing the caller on the display
	 * @param callId Id of the SipClient session the call belongs to
	 * @param name Caller name, if known
	 * @param number Caller number, if known
	 */
	async ringOn(callId: string, name?: string, number?: string) {
		const deviceId = this.activeDeviceId

		if (deviceId === undefined) { return }

		const contact = this.makeContact(name, number)
		const known = this.calls.get(callId)

		if (known) {
			// Already on the device - just refresh the caller shown on the display
			known.contact = contact
			const updated = await this.ipc.yealinkUpdateContact(deviceId, known.cid, contact)

			if (!updated.success) { logger.error(`YealinkElectron: Refreshing the display failed - ${updated.error?.message}`) }
			return
		}

		const call: MirroredCall = { cid: this.nextCid(), state: 'ringing', contact }

		this.calls.set(callId, call)
		logger.info(`YealinkElectron: Telling headset to start ringer - ${contact.name} ${contact.number}`)

		const res = await this.ipc.yealinkIncomingCall(deviceId, call.cid, contact)

		if (!res.success) { logger.error(`YealinkElectron: Start ringer failed - ${res.error?.message}`) }
	}

	/**
	 * Stop ringing for a call that is no longer offered
	 * @param callId Id of the SipClient session the call belongs to
	 */
	async ringOff(callId: string) {
		if (this.calls.get(callId)?.state === 'ringing') { await this.onHook(callId) }
	}

	/**
	 * Take the headset off hook for a call. Depending on the state the device has the call in this
	 * answers the ringing call, starts ringback for a new outgoing call, or moves the device's
	 * audio path to it - the last one is what gives a call started while another one is on hold
	 * audio in the headset.
	 * @param callId Id of the SipClient session the call belongs to
	 * @param name Name of the other party, if known
	 * @param number Number of the other party, if known
	 * @param answered True when the call has been answered (accepted), false while still in progress
	 */
	async offHook(callId: string, name?: string, number?: string, answered = false) {
		const deviceId = this.activeDeviceId

		if (deviceId === undefined) { return }

		const contact = this.makeContact(name, number)
		const call = this.adopt(callId)

		logger.info(`YealinkElectron: Telling headset to go off hook (${call?.state ?? 'call not on the device yet'})`)

		if (!call) {
			const fresh: MirroredCall = { cid: this.nextCid(), state: answered ? 'active' : 'ringback', contact }

			this.calls.set(callId, fresh)

			if (answered) {
				await this.activate(fresh, () => this.ipc.yealinkOutgoingCall(deviceId, fresh.cid, contact))
			} else {
				// Pre-answer: the app plays the ringing tone itself, so this must not take the
				// audio path from a call that is still up
				const res = await this.ipc.yealinkRingBack(deviceId, fresh.cid)

				if (!res.success) { logger.error(`YealinkElectron: Ringback failed - ${res.error?.message}`) }
			}
			return
		}

		switch (call.state) {
			case 'ringing':
				await this.activate(call, () => this.ipc.yealinkAnswerCall(deviceId, call.cid))
				// If another call than the ringing one was answered, refresh the display
				await this.refreshDisplay(call, contact)
				break
			case 'ringback':
				if (!answered) { return } // still in ringback (SIP 183 and 180 can both fire) - nothing to do
				// Keep a resolved contact (e.g. from a device phonebook dial) unless a real name is provided
				if (contact.name && contact.name !== contact.number) { call.contact = contact }
				await this.activate(call, () => this.ipc.yealinkOutgoingCall(deviceId, call.cid, call.contact))
				break
			case 'hold':
				// The app has the call off hook again while the device still holds it - without this
				// the headset stays silent for a call the device parked
				await this.activate(call, () => this.ipc.yealinkResumeCall(deviceId, call.cid))
				break
			case 'active':
				await this.refreshDisplay(call, contact)
				break
		}
	}

	/**
	 * Put the headset on hook - ends one call on the device, or every call it still shows when no
	 * call is named (the SIP client shutting down)
	 * @param callId Id of the SipClient session whose call to end, or undefined for all of them
	 */
	async onHook(callId?: string) {
		const deviceId = this.activeDeviceId

		// A call that failed before any SIP progress never claimed the call the user dialed on the
		// device - claim it here, or the device keeps showing a call that no longer exists
		if (callId !== undefined) { this.adopt(callId) }

		const ending = callId === undefined ? [...this.calls.keys()] : [callId]

		for (const id of ending) {
			const call = this.calls.get(id)

			if (!call) { continue }

			this.calls.delete(id)
			if (deviceId === undefined) { continue }

			logger.info('YealinkElectron: Telling headset to go on hook')

			const res = await this.ipc.yealinkTerminateCall(deviceId, call.cid)

			if (!res.success) { logger.error(`YealinkElectron: On hook failed - ${res.error?.message}`) }
		}

		this.runDeferredPhonebookReload()
	}

	/**
	 * Show a call as held on the device - the device parks its audio path for it
	 * @param callId Id of the SipClient session the call belongs to
	 */
	async hold(callId: string) {
		const deviceId = this.activeDeviceId
		const call = this.calls.get(callId)

		if (deviceId === undefined || !call) { return }
		// Already held: a swap acks the unhold of the call being resumed before the hold of the one
		// it replaces, and repeating the hold here would park the call the user just resumed
		if (call.state === 'hold') { return }

		logger.info('YealinkElectron: Telling headset to go on hold')
		call.state = 'hold'

		const res = await this.ipc.yealinkHoldCall(deviceId, call.cid, call.contact)

		if (!res.success) { logger.error(`YealinkElectron: Hold failed - ${res.error?.message}`) }
	}

	/**
	 * Resume a held call - the device moves its audio path back to it
	 * @param callId Id of the SipClient session the call belongs to
	 */
	async unhold(callId: string) {
		const deviceId = this.activeDeviceId
		const call = this.calls.get(callId)

		if (deviceId === undefined || !call || call.state === 'active') { return }

		logger.info('YealinkElectron: Telling headset to unhold')
		await this.activate(call, () => this.ipc.yealinkResumeCall(deviceId, call.cid))
	}

	/**
	 * Show a call as muted on the device - also the required ack when the user pressed Mute on the device
	 * @param callId Id of the SipClient session the call belongs to, or undefined for the call the device is on
	 */
	async mute(callId?: string) {
		const call = this.targetCall(callId)

		if (this.activeDeviceId === undefined || !call) { return }
		await this.ipc.yealinkMuteCall(this.activeDeviceId, call.cid)
	}

	/**
	 * Show a call as unmuted on the device - also the required ack when the user pressed Unmute on the device
	 * @param callId Id of the SipClient session the call belongs to, or undefined for the call the device is on
	 */
	async unmute(callId?: string) {
		const call = this.targetCall(callId)

		if (this.activeDeviceId === undefined || !call) { return }
		await this.ipc.yealinkUnmuteCall(this.activeDeviceId, call.cid)
	}

	// #endregion

	// #region Phonebook

	private schedulePhonebookSync() {
		if (this.phonebookTimer) { clearTimeout(this.phonebookTimer) }
		this.phonebookTimer = setTimeout(() => {
			this.phonebookTimer = undefined
			this.syncPhonebook()
		}, PHONEBOOK_SYNC_DEBOUNCE_MS)
	}

	/**
	 * Push contacts to the phonebook browsable on the device: local extensions (colleagues) first,
	 * then the user's personal phonebook, then fill up with the shared phonebook - capped at
	 * MAX_PHONEBOOK_ENTRIES because the device silently drops any overflow.
	 */
	private async syncPhonebook() {
		if (this.phonebookSyncRunning) {
			this.schedulePhonebookSync()
			return
		}

		const deviceId = this.activeDeviceId

		if (deviceId === undefined) {
			logger.debug('YealinkElectron: Phonebook sync skipped - no headset bound for call control')
			return
		}

		// The SDK's Contact feature flag is unreliable (reports unsupported on devices that do have
		// a phonebook) - assume every Yealink headset supports one and just push

		this.phonebookSyncRunning = true

		try {
			const employees = useEmployeesStore()
			const phonebook = usePhonebookStore()

			await employees.ready()
			await phonebook.ready()

			const contacts: YealinkContact[] = []

			// 1. Local extensions (colleagues)
			for (const employee of [...employees.employees].sort((a, b) => a.name.localeCompare(b.name))) {
				if (employee.me) { continue }

				const extension = employee.primaryExtension
				const number = extension?.number ?? extension?.direct ?? extension?.mobile[0]

				if (!employee.name || !extension || !number) { continue }

				contacts.push({
					name: employee.name,
					number: number,
					mobile: extension.mobile[0],
					other: extension.direct !== number ? extension.direct : undefined
				})
			}

			const colleagueCount = contacts.length

			// 2. The user's personal phonebook, 3. then fill up with the shared phonebook
			const personal = phonebook.contacts.filter(contact => contact.employee)
			const shared = phonebook.contacts.filter(contact => !contact.employee)
			let personalCount = 0

			for (const contact of [...personal, ...shared]) {
				const entry = this.phonebookEntryFrom(contact)

				if (entry) {
					contacts.push(entry)
					if (contact.employee) { personalCount++ }
				}
			}

			const list = contacts.slice(0, MAX_PHONEBOOK_ENTRIES)
			const fingerprint = `${deviceId}:${JSON.stringify(list)}`

			if (fingerprint === this.lastPushedPhonebook) {
				logger.debug(`YealinkElectron: Phonebook unchanged (${list.length} contacts) - not pushing to the device`)
				return
			}

			logger.info(`YealinkElectron: Loading ${list.length} contacts for the phonebook on ${this.useDeviceName.value}...`)

			// Delivery is pull-based: this only loads the list SDK-side - the device fetches it on
			// its own terms (attach/reboot handshake, session start, user reloading Contacts) and
			// MERGES every fetch, so a changed list also needs the reload cycle below to replace
			// what the device already fetched without duplicating entries
			const res = await this.ipc.yealinkSetPhonebook(deviceId, list)

			if (res.success) {
				this.pushedContacts = list
				this.lastPushedPhonebook = fingerprint
				logger.info(`YealinkElectron: Loaded ${list.length} contacts for the device phonebook ` +
					`(${Math.min(colleagueCount, list.length)} colleagues, ${Math.min(personalCount, Math.max(list.length - colleagueCount, 0))} personal, ` +
					`${Math.max(list.length - colleagueCount - personalCount, 0)} shared${contacts.length > list.length ? ` - ${contacts.length - list.length} dropped due to the device limit` : ''})`)
				void this.forcePhonebookReload()
			} else {
				logger.error(`YealinkElectron: Failed to load the phonebook for the device - ${res.error?.message}`)
			}
		}
		catch (err) {
			logger.error(`YealinkElectron: Phonebook sync failed - ${(err as Error).message}`)
		}
		finally {
			this.phonebookSyncRunning = false
		}
	}

	/**
	 * Cycle the Connecter registration so the device drops and re-fetches the phonebook - the only
	 * mid-session replace, because the device merges every fetch. The effect on an in-flight call
	 * is untested, so during a call the reload is deferred to the call end. When the Connecter
	 * refuses the re-registration (the headset is then unregistered until a cycle succeeds) one
	 * retry is scheduled. Older shells without the API keep the push-only behavior: the list lands
	 * at the device's next fetch (attach, reboot or session start).
	 * @param isRetry True when this call is the scheduled retry - no further retry on failure
	 */
	private async forcePhonebookReload(isRetry = false) {
		if (typeof this.ipc.yealinkForcePhonebookReload !== 'function') {
			logger.debug('YealinkElectron: The shell cannot force a phonebook reload - the device fetches the list at its next attach, reboot or session start')
			return
		}

		if (this.calls.size > 0) {
			this.phonebookReloadPending = true
			logger.info('YealinkElectron: Phonebook reload deferred until the call ends')
			return
		}

		this.phonebookReloadPending = false
		logger.info('YealinkElectron: Cycling the device connection so the phonebook is re-fetched')

		const res = await this.ipc.yealinkForcePhonebookReload()

		if (!res.success) {
			logger.error(`YealinkElectron: Phonebook reload failed - ${res.error?.message}`)
		} else if (res.result === false) {
			if (isRetry) {
				logger.error('YealinkElectron: The Connecter refused the phonebook reload again - giving up, the device phonebook may be stale until the next reconnect')
			} else {
				logger.warning(`YealinkElectron: The Connecter refused the phonebook reload (the headset is unregistered until a cycle succeeds) - retrying in ${PHONEBOOK_RELOAD_RETRY_MS / 1000}s`)
				setTimeout(() => { void this.forcePhonebookReload(true) }, PHONEBOOK_RELOAD_RETRY_MS)
			}
		}
	}

	/**
	 * Map a phonebook contact to a device phonebook entry
	 * @param contact Phonebook contact to map
	 * @returns The device entry, or undefined when the contact has no name or no number
	 */
	private phonebookEntryFrom(contact: PbxPhonebookContact): YealinkContact | undefined {
		if (!contact.name) { return undefined }

		const numbers = contact.phoneNumbers.filter(phoneNumber => phoneNumber.number)
		const main = numbers.find(phoneNumber => phoneNumber.type === PbxPhonebookContactPhoneNumberType.MAIN) ??
			numbers.find(phoneNumber => phoneNumber.type === PbxPhonebookContactPhoneNumberType.WORK) ??
			numbers.find(phoneNumber => phoneNumber.type !== PbxPhonebookContactPhoneNumberType.MOBILE) ??
			numbers[0]

		if (!main) { return undefined }

		const mobile = numbers.find(phoneNumber => phoneNumber.type === PbxPhonebookContactPhoneNumberType.MOBILE && phoneNumber !== main)
		const other = numbers.find(phoneNumber => phoneNumber !== main && phoneNumber !== mobile)

		return {
			name: contact.name,
			number: main.number,
			company: contact.company ?? undefined,
			mobile: mobile?.number,
			other: other?.number
		}
	}

	// #endregion

	// #region Helpers

	private nextCid() {
		this.cidCounter += 1
		return this.cidCounter
	}

	/** The call the device is currently carrying audio for, if any */
	private get activeCall() {
		return [...this.calls.values()].find(call => call.state === 'active')
	}

	/**
	 * The call an operation applies to: the named one, or the call the device is on when the caller
	 * has none (a button pressed on the device for a call it never told us a cid for)
	 * @param callId Id of the SipClient session, if known
	 */
	private targetCall(callId?: string) {
		return callId === undefined ? this.activeCall : this.calls.get(callId)
	}

	/**
	 * Make one call the call the device carries audio for. The device has a single audio path, so
	 * every other call it still considers active is held first - the order the SDK's multi-call API
	 * expects - and only then does the activating operation run.
	 * @param call The call to move the audio path to
	 * @param apply The operation that activates it on the device (answer, outgoing or resume)
	 */
	private async activate(call: MirroredCall, apply: () => Promise<YealinkResult>) {
		const deviceId = this.activeDeviceId as number

		for (const other of this.calls.values()) {
			if (other === call || other.state !== 'active') { continue }

			logger.info('YealinkElectron: Holding the call the headset was on - the device has one audio path')
			other.state = 'hold'

			const held = await this.ipc.yealinkHoldCall(deviceId, other.cid, other.contact)

			if (!held.success) { logger.error(`YealinkElectron: Hold failed - ${held.error?.message}`) }
		}

		call.state = 'active'

		const res = await apply()

		// Names the SDK function and code on failure: a device that cannot hold one call while
		// another is active would fail exactly here, and the headset would stay silent
		if (!res.success) {
			logger.error(`YealinkElectron: Activating the call on the headset failed - ${res.error?.sdkFunction ?? 'SDK'} returned ${res.error?.codeName ?? res.error?.code} - ${res.error?.message}`)
		}
	}

	/**
	 * Find the call the device has for a SIP session, adopting a call the user dialed on the device
	 * itself: that call reaches the device before its SIP session exists, so the placeholder is
	 * re-keyed here rather than a second call appearing on the display for the same conversation.
	 * @param callId Id of the SipClient session
	 */
	private adopt(callId: string): MirroredCall | undefined {
		const known = this.calls.get(callId)

		if (known) { return known }

		const dialed = this.calls.get(PENDING_DIAL)

		if (!dialed) { return undefined }

		this.calls.delete(PENDING_DIAL)
		this.calls.set(callId, dialed)
		return dialed
	}

	/**
	 * Refresh the party shown on the device when the call turned out to be with someone else
	 * @param call The call as mirrored on the device
	 * @param contact The party to show
	 */
	private async refreshDisplay(call: MirroredCall, contact: YealinkContact) {
		if (!contact.name && !contact.number) { return }
		if (contact.name === call.contact.name && contact.number === call.contact.number) { return }

		call.contact = contact

		const res = await this.ipc.yealinkUpdateContact(this.activeDeviceId as number, call.cid, contact)

		if (!res.success) { logger.error(`YealinkElectron: Refreshing the display failed - ${res.error?.message}`) }
	}

	/** Forget every call on the device - the device itself is released by the caller */
	private resetCallState() {
		this.calls.clear()
		this.runDeferredPhonebookReload()
	}

	/**
	 * Run a reload deferred by a call in progress, once the device has no calls left - delayed so
	 * the on-hook ack issued right before reaches the device ahead of the registration cycle that
	 * disconnects it. The flag clears here so back-to-back call ends cannot schedule two cycles.
	 */
	private runDeferredPhonebookReload() {
		if (!this.phonebookReloadPending || this.calls.size > 0) { return }

		this.phonebookReloadPending = false
		setTimeout(() => { void this.forcePhonebookReload() }, PHONEBOOK_RELOAD_AFTER_CALL_MS)
	}

	/** Build a contact for the device display - the device draws the avatar initials from the name, so fall back to the number */
	private makeContact(name?: string, number?: string): YealinkContact {
		return { name: name?.trim() || number || '', number: number ?? '' }
	}

	// #endregion
}
